import { FC, PropsWithChildren, useCallback, useEffect } from 'react'

import * as Sentry from '@sentry/react'
import {
  ErrorBoundaryProps,
  FallbackProps,
  ErrorBoundary as ReactErrorBoundary,
} from 'react-error-boundary'

import { DialogFallback } from './DialogFallback'
import { ModuleFallback } from './ModuleFallback'
import { PageFallback } from './PageFallback'

const fallbackTemplates: Record<string, FC<FallbackProps>> = {
  page: PageFallback,
  module: ModuleFallback,
  dialog: DialogFallback,
}

type Props = Pick<ErrorBoundaryProps, 'resetKeys' | 'onError' | 'onReset'> & {
  boundaryName: string
  fallbackTemplate: keyof typeof fallbackTemplates
}

interface ErrorBoundaryFallbackRendererProps extends FallbackProps {
  boundaryName: string
  fallbackTemplate: keyof typeof fallbackTemplates
}

export const ErrorBoundaryFallbackRenderer: FC<
  ErrorBoundaryFallbackRendererProps
> = ({ boundaryName, fallbackTemplate, ...fallbackProps }) => {
  const FallbackComponent = fallbackTemplates[fallbackTemplate]

  useEffect(() => {
    if (fallbackProps.error) {
      Sentry.captureException(fallbackProps.error, {
        tags: {
          boundaryName,
        },
      })
    }
  }, [fallbackProps.error, boundaryName])

  return <FallbackComponent {...fallbackProps} />
}

export const ErrorBoundary: FC<PropsWithChildren<Props>> = ({
  boundaryName,
  fallbackTemplate,
  children,
  resetKeys,
  onError,
  onReset,
}) => {
  const renderErrorBoundaryFallback = useCallback(
    (fallbackProps: FallbackProps) => {
      return (
        <ErrorBoundaryFallbackRenderer
          fallbackTemplate={fallbackTemplate}
          boundaryName={boundaryName}
          {...fallbackProps}
        />
      )
    },
    [boundaryName, fallbackTemplate],
  )

  return (
    <ReactErrorBoundary
      onError={onError}
      resetKeys={resetKeys}
      onReset={onReset}
      FallbackComponent={renderErrorBoundaryFallback}
    >
      {children}
    </ReactErrorBoundary>
  )
}
